import type { AllocationOperation, ApartmentBatchResponse } from '@sivan/shared';
import { Modal } from './Modal';
import { APARTMENT_OUTCOME_LABELS, count } from './labels';
import { Value } from './Value';

export type ReviewPhase = 'reviewing' | 'saving' | 'uncertain';

/** What the review shows for one draft row: its title and its field changes. */
export interface ReviewRow {
  readonly title: string;
  readonly changes: readonly string[];
}

export interface SheetReviewProps {
  readonly open: boolean;
  readonly phase: ReviewPhase;
  /** The Apartments and the space sheets answer the same shape. */
  readonly result: Pick<ApartmentBatchResponse, 'summary' | 'rows'> | null;
  /** The sheet's draft rows, by client row id. */
  readonly describe: (clientRowId: string) => ReviewRow | undefined;
  /** Rows are apartments (operations name spaces) or spaces (operations name apartments). */
  readonly perspective: 'apartment' | 'space';
  /** Labels of created and updated rows: [one, many]. */
  readonly labels: {
    readonly create: readonly [string, string];
    readonly update: readonly [string, string];
  };
  readonly errorText: (field: string, code: string) => string;
  /** Closes the review and selects the cell of an error (only for fields that are cells). */
  readonly onFocusCell?: ((clientRowId: string, field: string) => void) | undefined;
  readonly isCell?: ((field: string) => boolean) | undefined;
  /** Allocation ids whose ending the user confirmed in this dialog. */
  readonly confirmed: ReadonlySet<string>;
  readonly onToggleConfirm: (allocationId: string, checked: boolean) => void;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
  readonly onKeepMine: (key: string) => void;
  readonly onDiscard: (key: string) => void;
}

const PROBLEMS = new Set(['invalid', 'duplicate', 'conflict']);
const KIND = { parking: 'חניה', storage: 'מחסן' } as const;

/** A space as text; user values are isolated so "-2" is not reordered inside Hebrew text. */
function SpaceText({ op }: { readonly op: AllocationOperation }) {
  return (
    <>
      {KIND[op.kind]} <Value text={op.space.number} />
      {op.space.location !== '' && (
        <>
          {' · מרתף '}
          <Value text={op.space.location} />
        </>
      )}{' '}
      (<Value text={op.space.structureCode} />)
    </>
  );
}
const apartmentText = (a: AllocationOperation['apartment']) =>
  `בניין ${a.building || '—'} · דירה ${a.number || '—'}`;

function OperationLine({
  op,
  operations,
  confirmed,
  onToggle,
  perspective,
}: {
  readonly perspective: 'apartment' | 'space';
  readonly op: AllocationOperation;
  readonly operations: readonly AllocationOperation[];
  readonly confirmed: ReadonlySet<string>;
  readonly onToggle: (allocationId: string, checked: boolean) => void;
}) {
  const subject =
    perspective === 'apartment' ? <SpaceText op={op} /> : <>ל{apartmentText(op.apartment)}</>;
  if (op.action === 'retain')
    return (
      <li className="op op--retain">
        {perspective === 'apartment' ? 'נשארת ללא שינוי: ' : 'מוצמדת ללא שינוי '}
        {subject}
      </li>
    );
  if (op.action === 'assign') {
    const from = operations.find(
      (o) =>
        o.action === 'end' &&
        o.endReason === 'transferred' &&
        o.space.id !== null &&
        o.space.id === op.space.id,
    );
    return (
      <li className="op op--assign">
        {perspective === 'apartment' ? 'הצמדת ' : 'הצמדה '}
        {subject}
        {perspective === 'apartment' && op.newSpace && ' · מקום חדש ייווצר'}
        {from !== undefined && ` · מועברת מ${apartmentText(from.apartment)}`}
      </li>
    );
  }
  const id = op.allocationId ?? '';
  const why =
    op.endReason === 'transferred'
      ? `העברה: ${apartmentText(op.apartment)} מאבדת את ההצמדה`
      : op.endReason === 'replaced'
        ? 'מוחלפת במקום אחר'
        : 'נמחקה במפורש';
  return (
    <li className="op op--end">
      <span>
        {perspective === 'apartment' ? (
          <>
            סיום הצמדת <SpaceText op={op} /> — {why}.
          </>
        ) : (
          <>
            סיום ההצמדה {subject} —{' '}
            {op.endReason === 'transferred' ? 'המקום עובר לדירה אחרת' : 'נמחקה במפורש'}.
          </>
        )}{' '}
        ההצמדה תישמר בהיסטוריה.
      </span>
      <label className="op__confirm">
        <input
          type="checkbox"
          checked={confirmed.has(id)}
          onChange={(e) => {
            onToggle(id, e.target.checked);
          }}
        />
        <span>
          {op.endReason === 'transferred' ? 'אני מאשר/ת את ההעברה' : 'אני מאשר/ת את סיום ההצמדה'}
        </span>
      </label>
    </li>
  );
}

/**
 * One unified preview of a batch (docs/16 §6.3): for each row (an apartment or a space), its field
 * changes and every allocation it keeps, gets or loses. Ends and transfers need an explicit tick
 * each; nothing is written before "שמירה", and the save is all or nothing.
 */
export function SheetReview(props: SheetReviewProps) {
  const { result, phase, confirmed, labels } = props;
  const problems = result?.rows.filter((r) => PROBLEMS.has(r.status)) ?? [];
  const changes =
    result?.rows.filter((r) => !PROBLEMS.has(r.status) && r.status !== 'unchanged') ?? [];
  const ends = changes.flatMap((r) => r.allocations.filter((a) => a.action === 'end'));
  const allConfirmed = ends.every((a) => a.allocationId !== null && confirmed.has(a.allocationId));
  const canSave =
    result !== null &&
    problems.length === 0 &&
    changes.length > 0 &&
    allConfirmed &&
    phase !== 'saving';
  const s = result?.summary;

  return (
    <Modal title="בדיקת השינויים לפני שמירה" open={props.open} onClose={props.onClose} wide>
      {result === null || s === undefined ? (
        <p role="status">בודק את השינויים מול השרת…</p>
      ) : (
        <>
          <ul className="summary-chips" aria-label="סיכום השינויים">
            {s.create > 0 && (
              <li className="chip chip--create">{count(s.create, ...labels.create)}</li>
            )}
            {s.update + s.confirm > 0 && (
              <li className="chip chip--update">{count(s.update + s.confirm, ...labels.update)}</li>
            )}
            {s.assign > 0 && (
              <li className="chip chip--create">{count(s.assign, 'הצמדה חדשה', 'הצמדות חדשות')}</li>
            )}
            {s.spaces > 0 && (
              <li className="chip chip--create">
                {count(s.spaces, 'מקום חדש ייווצר', 'מקומות חדשים ייווצרו')}
              </li>
            )}
            {s.end > 0 && (
              <li className="chip chip--warning">
                {count(s.end, 'הצמדה מסתיימת', 'הצמדות מסתיימות')}
              </li>
            )}
            {s.unchanged > 0 && <li className="chip chip--stored">ללא שינוי: {s.unchanged}</li>}
            {problems.length > 0 && (
              <li className="chip chip--error">
                {count(problems.length, 'שורה עם בעיה', 'שורות עם בעיות')}
              </li>
            )}
          </ul>

          {problems.length > 0 && (
            <section aria-labelledby="problems-title">
              <h3 id="problems-title">שגיאות שחוסמות את השמירה</h3>
              <table className="problem-table">
                <thead>
                  <tr>
                    <th scope="col">דירה</th>
                    <th scope="col">מצב</th>
                    <th scope="col">פירוט</th>
                    <th scope="col">פעולה</th>
                  </tr>
                </thead>
                <tbody>
                  {problems.map((problem) => (
                    <tr key={problem.clientRowId}>
                      <td>{props.describe(problem.clientRowId)?.title ?? ''}</td>
                      <td>{APARTMENT_OUTCOME_LABELS[problem.status]}</td>
                      <td>
                        <ul className="problem-fields">
                          {Object.entries(problem.fields).map(([field, code]) => (
                            <li key={field}>
                              <span>{props.errorText(field, code)}</span>
                              {props.onFocusCell !== undefined &&
                                props.isCell?.(field) === true && (
                                  <button
                                    type="button"
                                    className="button button--small button--ghost"
                                    onClick={() => {
                                      props.onFocusCell?.(problem.clientRowId, field);
                                    }}
                                  >
                                    מעבר לתא
                                  </button>
                                )}
                            </li>
                          ))}
                        </ul>
                      </td>
                      <td className="problem-actions">
                        {problem.status === 'conflict' &&
                          problem.fields['rowVersion'] === 'stale' && (
                            <button
                              type="button"
                              className="button button--small"
                              onClick={() => {
                                props.onKeepMine(problem.clientRowId);
                              }}
                            >
                              החלת השינוי שלי על הגרסה העדכנית
                            </button>
                          )}
                        <button
                          type="button"
                          className="button button--small button--ghost"
                          onClick={() => {
                            props.onDiscard(problem.clientRowId);
                          }}
                        >
                          ביטול השינוי בשורה
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="hint">
                תקנו את התאים המסומנים בגיליון ובדקו שוב. הטיוטה נשמרת עד לשמירה.
              </p>
            </section>
          )}

          {changes.length > 0 && (
            <section aria-labelledby="changes-title">
              <h3 id="changes-title">מה יישמר</h3>
              <ol className="change-list">
                {changes.map((change) => {
                  const row = props.describe(change.clientRowId);
                  const lines = row?.changes ?? [];
                  return (
                    <li key={change.clientRowId} className="change-card">
                      <div className="change-card__head">
                        <strong>{row?.title ?? ''}</strong>
                        <span
                          className={`chip chip--${change.status === 'confirm' ? 'warning' : change.status}`}
                        >
                          {APARTMENT_OUTCOME_LABELS[change.status]}
                        </span>
                      </div>
                      <ul className="change-card__ops">
                        {lines.map((line) => (
                          <li key={line} className="op op--field">
                            {line}
                          </li>
                        ))}
                        {change.allocations.map((op) => (
                          <OperationLine
                            perspective={props.perspective}
                            key={`${op.action}:${op.allocationId ?? op.space.id}`}
                            op={op}
                            operations={change.allocations}
                            confirmed={confirmed}
                            onToggle={props.onToggleConfirm}
                          />
                        ))}
                      </ul>
                    </li>
                  );
                })}
              </ol>
              <p className="hint">
                {ends.length > 0 && !allConfirmed
                  ? 'אזהרה: יש לאשר כל סיום הצמדה והעברה לפני השמירה. '
                  : ''}
                לא יימחק או יסתיים דבר מעבר למה שמפורט כאן. השמירה מתבצעת כולה, או לא מתבצעת כלל.
              </p>
            </section>
          )}
          {problems.length === 0 && changes.length === 0 && <p>אין שינויים לשמירה.</p>}
          {phase === 'uncertain' && (
            <p role="alert" className="notice notice--warning">
              החיבור לשרת נקטע ולא ידוע אם השמירה הושלמה. ניסיון חוזר בטוח: אותה פעולה לא תיכתב
              פעמיים.
            </p>
          )}
        </>
      )}
      <footer className="modal__footer">
        <button
          type="button"
          className="button button--primary"
          disabled={!canSave}
          onClick={props.onConfirm}
        >
          {phase === 'saving' ? 'שומר…' : phase === 'uncertain' ? 'ניסיון חוזר' : 'שמירה'}
        </button>
        <button
          type="button"
          className="button"
          onClick={props.onClose}
          disabled={phase === 'saving'}
        >
          חזרה לעריכה
        </button>
      </footer>
    </Modal>
  );
}
